import { isMobileViewport } from '@/lib/utils/isMobileViewport';
import logger from '@/utils/logger';
import { clearPreviousSignIn } from './clearPreviousSignIn';
import {
  AUTH0_AUDIENCE,
  AUTH0_CONNECTION_DB,
  AUTH0_SCOPE,
  DEFAULT_POST_LOGIN_PATH,
  GRANT_TYPES,
  getAuth0ClientId,
  getAuth0Domain,
  LOGIN_METHODS,
} from './constants';
import { removeLoginMethod, setAuthInProgress, setLoginMethod, storeTokenResponse } from './tokenStorage';
import type { TokenResponse } from './types';

// gloss
export const loginWithEmailAndPassword = async (email: string, password: string): Promise<TokenResponse> => {
  try {
    const response = await fetch(`${getAuth0Domain()}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: GRANT_TYPES.PASSWORD_REALM,
        client_id: getAuth0ClientId(),
        username: email,
        password,
        scope: AUTH0_SCOPE,
        audience: AUTH0_AUDIENCE,
        realm: AUTH0_CONNECTION_DB,
      }),
    });

    const data = await response.json().catch((err: unknown) => {
      throw new Error(`Invalid response from Auth0: ${err instanceof Error ? err.message : 'parse failed'}`);
    });

    if (response.ok) {
      if (!data.refresh_token || !data.id_token) {
        throw new Error('Auth0 response missing required tokens');
      }
      clearPreviousSignIn();
      setLoginMethod(LOGIN_METHODS.DB);
      storeTokenResponse(data);
      return { accessToken: data.access_token, refreshToken: data.refresh_token, idToken: data.id_token };
    }

    if (data?.error) {
      throwUserFriendlyAuthError(data);
    }

    throw new Error(data?.error_description || 'Authentication failed');
  } catch (e) {
    logger.error(e, { context: 'loginWithEmailAndPassword' });
    throw e;
  }
};

// gloss
export const emailPasswordSignup = async (email: string, password: string, firstName = '', lastName = '') => {
  try {
    const response = await fetch(`${getAuth0Domain()}/dbconnections/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        client_id: getAuth0ClientId(),
        email,
        password,
        connection: AUTH0_CONNECTION_DB,
        given_name: firstName || email.split('@')[0],
        family_name: lastName || 'User',
        name: `${firstName} ${lastName}`.trim() || email.split('@')[0],
      }),
    });

    const contentType = response.headers.get('content-type');
    const data = contentType?.includes('application/json')
      ? await response.json()
      : { error: 'Invalid response format', details: await response.text() };

    return { response, data };
  } catch (e) {
    logger.error(e, { context: 'emailPasswordSignup' });
    throw e;
  }
};

// gloss
export const startDbRedirect = async (
  loginWithRedirect: (opts: unknown) => Promise<void>,
  screenHint: 'login' | 'signup',
) => {
  try {
    clearPreviousSignIn();
    setAuthInProgress(true);
    setLoginMethod(LOGIN_METHODS.SERVICE);
    await loginWithRedirect({
      authorizationParams: {
        audience: AUTH0_AUDIENCE,
        scope: AUTH0_SCOPE,
        screen_hint: screenHint,
        connection: AUTH0_CONNECTION_DB,
      },
      appState: { returnTo: isMobileViewport() ? '/Home' : DEFAULT_POST_LOGIN_PATH },
    });
  } catch (error) {
    setAuthInProgress(false);
    removeLoginMethod();
    logger.error(error, { context: `startDb${screenHint === 'login' ? 'Login' : 'Signup'}` });
    throw error;
  }
};

// gloss
const throwUserFriendlyAuthError = (data: { error: string; error_description?: string }): never => {
  if (data.error_description?.includes('Wrong email or password')) {
    throw new Error('Invalid email or password. Please try again.');
  }
  if (data.error === 'invalid_grant' && data.error_description?.includes('email')) {
    throw new Error('Please verify your email address before signing in. Check your inbox for a verification email.');
  }
  if (data.error === 'invalid_user_password') {
    throw new Error('Invalid email or password. Please try again.');
  }
  if (data.error === 'invalid_grant' && data.error_description?.includes('Unknown or invalid user')) {
    throw new Error('Account not found. Please create an account first.');
  }
  throw new Error(data.error_description || data.error || 'Authentication failed');
};
